/**
 * SMS Management Interface Component
 * Handles SMS conversations, sending/receiving messages, MMS, deletion
 */

import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { authFetch } from '../../auth';
import useSessionPresenceWebSocket from '../../utils/useSessionPresenceWebSocket';
import CallerInfoButton from '../CallerInfo/CallerInfoButton';
import './SMSInterface.css';

// Resolve phone number to contact name
// Normalize a user-typed/pasted phone to E.164. Mirrors the dialer + server normalizers
// so a recipient field never carries a garbage value into the send endpoint.
const normalizeToE164 = (raw) => {
  if (!raw) return '';
  var s = String(raw).trim()
    .replace(/^(tel:|callto:|sip:|sms:)/i, '')
    .replace(/(?:[xX]|ext\.?|extension)\s*\d+\s*$/i, '')
    .replace(/[^\d+]/g, '');
  if (!s) return '';
  if (s.startsWith('+')) {
    if (/^\+\d{10}$/.test(s)) return '+1' + s.substring(1);  // missing US country code
    return s;
  }
  if (/^1\d{10}$/.test(s)) return '+' + s;
  if (/^\d{10}$/.test(s)) return '+1' + s;
  if (/^\d{7,15}$/.test(s)) return '+' + s;
  return s;
};

const formatPhoneDisplay = (phone) => {
  if (!phone) return '?';
  // Self-correct '+ then 10 digits' before formatting so the display matches what will be dialed.
  if (/^\+\d{10}$/.test(String(phone || '').trim())) phone = '+1' + String(phone).trim().substring(1);
  const digits = String(phone).replace(/[^\d]/g, '');
  if (digits.length === 10) return `(${digits.slice(0,3)}) ${digits.slice(3,6)}-${digits.slice(6)}`;
  if (digits.length === 11 && digits[0] === '1') return `(${digits.slice(1,4)}) ${digits.slice(4,7)}-${digits.slice(7)}`;
  return phone;
};

const getInitial = (name) => {
  if (!name) return '?';
  // Skip formatting chars like ( and +
  const clean = name.replace(/^[^a-zA-Z0-9]+/, '');
  return clean.charAt(0).toUpperCase() || '?';
};

const SMSInterface = ({ userPhoneNumbers = [] }) => {
  const location = useLocation();
  const [conversations, setConversations] = useState([]);
  const [activeConversation, setActiveConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedPhoneNumber, setSelectedPhoneNumber] = useState('all');
  const [newContact, setNewContact] = useState('');
  const [composing, setComposing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteMenuId, setDeleteMenuId] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [attachmentPreview, setAttachmentPreview] = useState(null);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [contactMap, setContactMap] = useState({});
  const activeConvRef = useRef(null);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const newMsgInputRef = useRef(null);
  const showConversationPane = Boolean(activeConversation || composing);

  // Keep ref in sync so WS callback sees latest value
  useEffect(() => { activeConvRef.current = activeConversation; }, [activeConversation]);

  // Normalize phone numbers — freenumbers can be strings or objects
  const phoneNumbers = useMemo(() => {
    return userPhoneNumbers.map(p => typeof p === 'string' ? p : p.number || p);
  }, [userPhoneNumbers]);

  // Load contacts for phone→name resolution
  useEffect(() => {
    authFetch('/api/contacts').then(r => r.ok ? r.json() : []).then(contacts => {
      const map = {};
      (contacts || []).forEach(c => {
        if (c.phone) {
          const digits = c.phone.replace(/[^\d]/g, '');
          if (c.name) { map[digits] = c.name; map['+' + digits] = c.name; if (digits.length === 10) map['1' + digits] = c.name; if (digits.startsWith('1') && digits.length === 11) map[digits.slice(1)] = c.name; }
        }
      });
      setContactMap(map);
    }).catch(() => {});
  }, []);

  // Auto-start SMS from contact-page nav OR URL params (sms:/?to=/?phone=/?number=)
  // so OS protocol handlers that route into the app land on the right conversation.
  // CRITICAL: must consume ONCE per incoming nav. Without the autoConsumedRef, a WS
  // new_message → loadConversations → conversations state update → this effect would
  // re-fire and re-select the original target, snapping the user's selection back to
  // whatever was in location.state.to. That manifested as "click a conversation, see it
  // for a moment, then UI bounces back to the previous one."
  var autoConsumedRef = useRef(false);
  useEffect(() => {
    if (autoConsumedRef.current) return;
    var to = location.state && location.state.to;
    if (!to) {
      try {
        var qs = new URLSearchParams(location.search || '');
        to = qs.get('to') || qs.get('sms') || qs.get('phone') || qs.get('number');
      } catch(e) {}
      if (to) to = String(to).replace(/^(tel:|callto:|sip:|sms:)/i, '');
    }
    if (!to || loading) return;
    autoConsumedRef.current = true;
    // Clear the URL search/state so a manual reload doesn't re-trigger.
    try { window.history.replaceState({}, '', location.pathname); } catch (_) {}
    to = String(to).replace(/[^\d+]/g, '');
    if (!to) return;
    if (!to.startsWith('+')) to = '+' + to;
    // Check if a conversation with this number already exists
    var existing = conversations.find(function(c) {
      return String(c.conversationWith).replace(/[^\d+]/g, '') === to;
    });
    if (existing) {
      setActiveConversation(existing);
      setComposing(false);
      return;
    }
    // Start new conversation
    setNewContact(to);
    setComposing(true);
    setActiveConversation(null);
    // Auto-select from number if user has exactly one
    if (phoneNumbers.length === 1) {
      setSelectedPhoneNumber(phoneNumbers[0]);
    }
    // Focus input once rendered
    setTimeout(function() {
      if (newMsgInputRef.current) newMsgInputRef.current.focus();
    }, 100);
  }, [location.state, location.search, location.pathname, loading, conversations, phoneNumbers]);

  // Resolve phone to contact name
  const resolveName = (phone) => {
    if (!phone) return '?';
    if (phone.toLowerCase() === 'linktime') return 'LinkTime Room';
    const digits = phone.replace(/[^\d]/g, '');
    return contactMap[digits] || contactMap['+' + digits] || contactMap['1' + digits] || contactMap[digits.replace(/^1/, '')] || formatPhoneDisplay(phone);
  };

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleWsMessage = useCallback((event) => {
    try {
      const msg = JSON.parse(event.data);
      console.log('WS message received:', msg.type, msg);

      if (msg.type === 'new_message' && msg.data) {
        const d = msg.data;
        console.log('New SMS via WS:', d.From, '->', d.To, d.Body);
        loadConversations();

        const conv = activeConvRef.current;
        if (conv) {
          const normalize = (n) => n && n.startsWith('+') ? n : '+' + n;
          const from = normalize(d.From || d.from || '');
          const to = normalize(d.To || d.to || '');
          const convWith = normalize(conv.conversationWith);
          const userNum = normalize(conv.userNumber);

          if ((from === convWith && to === userNum) || (from === userNum && to === convWith)) {
            setMessages(prev => [...prev, {
              id: d._id || d.MessageSid || `msg_${Date.now()}`,
              body: d.Body || d.body,
              from: d.From || d.from,
              to: d.To || d.to,
              dateCreated: d.DateCreated || d.dateCreated || new Date(),
              direction: d.direction || 'inbound',
              localRoute: d.localRoute || false,
              status: d.status || 'received',
              aiBot: !!d.aiBot,
              mediaUrl: d.mediaUrl || d.MediaUrl0 || null,
              mediaContentType: d.MediaContentType0 || null,
              numMedia: d.NumMedia || 0
            }]);
          }
        }
      }

      // Handle delivery status updates
      if (msg.type === 'message_status' && msg.data) {
        const { sid, status } = msg.data;
        setMessages(prev => prev.map(m =>
          (m.sid === sid || m.id === sid) ? { ...m, status } : m
        ));
      }
    } catch (e) {
      // ignore non-JSON messages
    }
  }, []);

  useSessionPresenceWebSocket(true, 'SMS', handleWsMessage);

  useEffect(() => {
    loadConversations(true);
  }, []);

  useEffect(() => {
    if (activeConversation) {
      loadMessages(activeConversation);
      // Mark messages as read when opening conversation
      authFetch('/api/conversations/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ conversationWith: activeConversation.conversationWith })
      }).then(() => loadConversations()).catch(() => {});
    }
  }, [activeConversation]);

  // Close delete menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setDeleteMenuId(null);
    if (deleteMenuId) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [deleteMenuId]);

  // Filter conversations by selected phone number
  const filteredConversations = useMemo(() => {
    if (selectedPhoneNumber === 'all') return conversations;
    const normalize = (n) => n && n.startsWith('+') ? n : '+' + n;
    const selected = normalize(selectedPhoneNumber);
    return conversations.filter(c => normalize(c.userNumber) === selected);
  }, [conversations, selectedPhoneNumber]);

  const loadConversations = async (showSpinner) => {
    try {
      if (showSpinner) setLoading(true);
      const response = await authFetch('/api/conversations');

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setConversations(data);
      setError(null);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Unable to load conversations');
      setConversations([]);
    } finally {
      if (showSpinner) setLoading(false);
    }
  };

  const loadMessages = async (conversation) => {
    try {
      const targetPhone = conversation.conversationWith;
      const userPhone = conversation.userNumber;

      let url = `/api/conversations/${encodeURIComponent(targetPhone)}/messages`;
      if (userPhone) {
        url += `?userNumber=${encodeURIComponent(userPhone)}`;
      }

      const response = await authFetch(url);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      setMessages(data);
    } catch (err) {
      console.error('Error loading messages:', err);
      setMessages([]);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();

    // If there's an attachment, send as MMS
    if (attachment) {
      return sendMMS(e);
    }

    if (!newMessage.trim()) return;

    // Reply goes from the number the contact actually reached (this conversation's
    // own number), NOT the global number filter — otherwise the reply leaves from a
    // different (often unregistered) number and silently fails to deliver.
    const fromNumber = activeConversation
      ? (activeConversation.userNumber || (selectedPhoneNumber !== 'all' ? selectedPhoneNumber : phoneNumbers[0]))
      : (selectedPhoneNumber !== 'all' ? selectedPhoneNumber : phoneNumbers[0]);

    if (!fromNumber) return;

    try {
      var rawTo = activeConversation ? activeConversation.conversationWith : newContact;
      var toNormalized = normalizeToE164(rawTo) || rawTo;
      // Reflect the normalized value back to the input so the user sees what's actually being sent.
      if (!activeConversation && toNormalized !== newContact) setNewContact(toNormalized);
      const messageData = {
        to: toNormalized,
        from: fromNumber,
        body: newMessage.trim()
      };

      const response = await authFetch('/api/conversations/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(messageData)
      });

      // Try to parse the body even on !ok so the server's friendly error reason surfaces
      // (e.g. "That phone number isn't valid…") instead of a bare HTTP 500.
      var data = null;
      try { data = await response.json(); } catch (_) {}
      if (!response.ok) {
        var reason = (data && (data.error || data.message)) || (response.status + ' ' + response.statusText);
        throw new Error(reason);
      }

      if (data.success) {
        const newMsg = {
          id: data.messageId || `msg_${Date.now()}`,
          body: newMessage,
          from: fromNumber,
          to: messageData.to,
          dateCreated: new Date(),
          direction: data.localRoute ? 'outbound-local' : 'outbound',
          localRoute: data.localRoute || false,
          status: data.localRoute ? 'delivered' : 'sent',
          sid: data.messageId
        };

        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');

        // If composing a new message, switch to the conversation so replies show up
        if (!activeConversation && messageData.to) {
          const normTo = (n) => {
            var c = (n || '').replace(/[\s\-\(\)\.]/g, '');
            if (/^\d{10}$/.test(c)) return '+1' + c;
            if (/^1\d{10}$/.test(c)) return '+' + c;
            return c.startsWith('+') ? c : '+' + c;
          };
          const normFrom = (n) => n && n.startsWith('+') ? n : '+' + n;
          const newConv = {
            conversationWith: normTo(messageData.to),
            userNumber: normFrom(fromNumber),
            lastMessage: newMessage,
            lastMessageDate: new Date(),
            unreadCount: 0
          };
          setActiveConversation(newConv);
          setComposing(false);
          setNewContact('');
          // Reload messages after a beat to pick up any quick replies
          setTimeout(() => loadMessages(newConv), 3000);
        }

        loadConversations();
      } else {
        throw new Error(data.error || 'Failed to send message');
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message: ' + err.message);
    }
  };

  const sendMMS = async () => {
    if (!attachment) return;

    // Same rule as text: send from this conversation's own number, not the filter.
    const fromNumber = activeConversation
      ? (activeConversation.userNumber || (selectedPhoneNumber !== 'all' ? selectedPhoneNumber : phoneNumbers[0]))
      : (selectedPhoneNumber !== 'all' ? selectedPhoneNumber : phoneNumbers[0]);

    if (!fromNumber) return;

    const toNumber = activeConversation ? activeConversation.conversationWith : newContact;
    if (!toNumber) return;

    try {
      const formData = new FormData();
      formData.append('media', attachment);
      formData.append('to', toNumber);
      formData.append('from', fromNumber);
      if (newMessage.trim()) {
        formData.append('body', newMessage.trim());
      }

      const response = await authFetch('/api/conversations/send-mms', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();

      if (data.success) {
        const newMsg = {
          id: data.messageId || `msg_${Date.now()}`,
          body: newMessage.trim(),
          from: fromNumber,
          to: toNumber,
          dateCreated: new Date(),
          direction: 'outbound',
          status: 'sent',
          sid: data.messageId,
          mediaUrl: data.mediaUrl || attachmentPreview,
          mediaContentType: attachment.type,
          numMedia: 1
        };

        setMessages(prev => [...prev, newMsg]);
        setNewMessage('');
        clearAttachment();
        loadConversations();
      } else {
        throw new Error(data.error || 'Failed to send MMS');
      }
    } catch (err) {
      console.error('Error sending MMS:', err);
      alert('Failed to send MMS: ' + err.message);
    }
  };

  const deleteMessage = async (messageId, mode) => {
    try {
      const response = await authFetch(`/api/conversations/messages/${messageId}?mode=${mode}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      if (data.success) {
        setMessages(prev => prev.filter(m => m.id !== messageId));
        setDeleteMenuId(null);
      }
    } catch (err) {
      console.error('Error deleting message:', err);
      alert('Failed to delete message: ' + err.message);
    }
  };

  const handleAttachmentSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setAttachment(file);

    // Create preview for images
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = (ev) => setAttachmentPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setAttachmentPreview(null);
    }
  };

  const clearAttachment = () => {
    setAttachment(null);
    setAttachmentPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const startNewConversation = () => {
    setActiveConversation(null);
    setMessages([]);
    setNewContact('');
    setComposing(true);
  };

  const closeConversationPane = () => {
    setActiveConversation(null);
    setMessages([]);
    setNewContact('');
    setNewMessage('');
    clearAttachment();
    setComposing(false);
  };

  const formatTime = (date) => {
    if (!date) return '';
    const now = new Date();
    const messageDate = new Date(date);
    if (isNaN(messageDate.getTime())) return '';

    if (now.toDateString() === messageDate.toDateString()) {
      return messageDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      const sameYear = now.getFullYear() === messageDate.getFullYear();
      const dateOpts = sameYear
        ? { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }
        : { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' };
      return messageDate.toLocaleDateString([], dateOpts);
    }
  };

  const conversationKey = (conv) => {
    return `${conv.conversationWith}|${conv.userNumber}`;
  };

  const isImageType = (contentType) => {
    if (!contentType) return false;
    return /^image\/(jpeg|jpg|png|gif|webp)$/i.test(contentType);
  };

  const isVideoType = (contentType) => {
    if (!contentType) return false;
    return /^video\//i.test(contentType);
  };

  const renderMedia = (message) => {
    if (!message.mediaUrl || message.numMedia < 1) return null;

    const url = message.mediaUrl;
    const type = message.mediaContentType || '';

    if (isImageType(type) || (!type && /\.(jpg|jpeg|png|gif|webp)(\?|$)/i.test(url))) {
      return (
        <div className="message-media">
          <img
            src={url}
            alt="MMS image"
            className="mms-image"
            onClick={() => setLightboxUrl(url)}
            loading="lazy"
          />
        </div>
      );
    }

    if (isVideoType(type)) {
      return (
        <div className="message-media">
          <video controls className="mms-video" preload="metadata">
            <source src={url} type={type} />
            Your browser does not support video playback.
          </video>
        </div>
      );
    }

    // Fallback: download link for other file types
    return (
      <div className="message-media">
        <a href={url} target="_blank" rel="noopener noreferrer" className="mms-file-link">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
          <span>View attachment</span>
        </a>
      </div>
    );
  };

  const statusIcon = (status, readStatus) => {
    if (readStatus === 'read') return '\u2713\u2713';
    switch (status) {
      case 'delivered': return '\u2713\u2713';
      case 'sent': return '\u2713';
      case 'read': return '\u2713\u2713';
      case 'failed': case 'undelivered': return '\u2717';
      case 'queued': case 'sending': return '\u23F3';
      default: return '\u2713';
    }
  };

  const statusClass = (status, readStatus) => {
    if (readStatus === 'read') return 'read';
    switch (status) {
      case 'delivered': case 'read': return 'delivered';
      case 'failed': case 'undelivered': return 'failed';
      case 'queued': case 'sending': return 'pending';
      default: return 'sent';
    }
  };

  if (loading) {
    return (
      <div className="sms-interface">
        <div className="loading">
          <div className="loading-spinner"></div>
          <p>Loading SMS conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`sms-interface${showConversationPane ? ' mobile-chat' : ''}`}>
      <div className="sms-sidebar">
        <div className="sms-header">
          <h2>Messages</h2>
          <button className="btn btn-primary" onClick={startNewConversation}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg> New
          </button>
        </div>

        {phoneNumbers.length > 1 && (
          <div className="phone-selector">
            <select
              value={selectedPhoneNumber}
              onChange={(e) => {
                setSelectedPhoneNumber(e.target.value);
                setActiveConversation(null);
                setMessages([]);
              }}
            >
              <option value="all">All Numbers</option>
              {phoneNumbers.map(num => (
                <option key={num} value={num}>{num}</option>
              ))}
            </select>
          </div>
        )}

        <div className="conversations-list">
          {filteredConversations.length === 0 ? (
            <div className="no-conversations">
              <p>No conversations yet</p>
            </div>
          ) : (
            filteredConversations.map(conversation => (
              <div
                key={conversationKey(conversation)}
                className={`conversation-item ${activeConversation && conversationKey(activeConversation) === conversationKey(conversation) ? 'active' : ''}`}
                onClick={() => { setActiveConversation(conversation); setComposing(false); }}
              >
                <div className="conversation-avatar">
                  {getInitial(resolveName(conversation.conversationWith), conversation.conversationWith)}
                </div>
                <div className="conversation-details">
                  <div className="conversation-header">
                    <span className="contact-name">
                      {resolveName(conversation.conversationWith)}
                      <CallerInfoButton number={conversation.conversationWith} size="xs" />
                      {resolveName(conversation.conversationWith) !== formatPhoneDisplay(conversation.conversationWith) && (
                        <span className="contact-phone-sub">{formatPhoneDisplay(conversation.conversationWith)}</span>
                      )}
                    </span>
                    {conversation.channel === 'whatsapp' && (
                      <span className="channel-badge whatsapp" title="WhatsApp">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="#25D366"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z"/><path d="M12 0C5.373 0 0 5.373 0 12c0 2.625.846 5.059 2.284 7.034L.789 23.49l4.624-1.474A11.932 11.932 0 0012 24c6.627 0 12-5.373 12-12S18.627 0 12 0zm0 21.813c-2.188 0-4.219-.7-5.875-1.888l-.422-.281-2.738.874.875-2.669-.313-.453A9.759 9.759 0 012.188 12c0-5.406 4.406-9.813 9.812-9.813 5.407 0 9.813 4.407 9.813 9.813 0 5.406-4.406 9.813-9.813 9.813z"/></svg>
                      </span>
                    )}
                    <span className="message-time">
                      {formatTime(conversation.lastMessageDate)}
                    </span>
                    {conversation.unreadCount > 0 && (
                      <span className="unread-badge">{conversation.unreadCount}</span>
                    )}
                  </div>
                  <div className="last-message">
                    {conversation.lastMessage}
                  </div>
                  {phoneNumbers.length > 1 && selectedPhoneNumber === 'all' && (
                    <div className="conversation-number">{conversation.userNumber}</div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      <div className="sms-chat">
        {activeConversation || composing ? (
          <>
            <div className="chat-header">
              <div className="chat-header-main">
                <button
                  type="button"
                  className="chat-back-button"
                  onClick={closeConversationPane}
                  aria-label="Back to conversations"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg>
                </button>
                <div className="chat-header-copy">
                  <h3>
                    {activeConversation
                      ? resolveName(activeConversation.conversationWith)
                      : (newContact ? formatPhoneDisplay(newContact) : 'New Message')
                    }
                    {activeConversation && <CallerInfoButton number={activeConversation.conversationWith} size="xs" />}
                    {activeConversation && resolveName(activeConversation.conversationWith) !== formatPhoneDisplay(activeConversation.conversationWith) && (
                      <span className="chat-header-phone">{formatPhoneDisplay(activeConversation.conversationWith)}</span>
                    )}
                  </h3>
                  <div className="chat-info">
                    <span>From: </span>
                    {activeConversation ? (
                      <span>{activeConversation.userNumber}</span>
                    ) : (
                      <select
                        value={selectedPhoneNumber !== 'all' ? selectedPhoneNumber : (phoneNumbers[0] || '')}
                        onChange={(e) => setSelectedPhoneNumber(e.target.value)}
                        className="from-number-select"
                      >
                        {phoneNumbers.map(num => (
                          <option key={num} value={num}>{num}</option>
                        ))}
                      </select>
                    )}
                  </div>
                </div>
              </div>
            </div>

            <div className="messages-container">
              {messages.map(message => {
                const isOutbound = message.direction === 'outbound' || message.direction === 'outbound-local' || message.direction === 'outbound-api';
                const isLocal = message.localRoute || message.direction === 'outbound-local';
                const isCmd = message.isCommand || message.isSystemAck;
                const isAi = !!message.aiBot;
                return (
                  <div
                    key={message.id}
                    className={`message ${isOutbound ? 'outbound' : 'inbound'} ${isCmd ? 'command' : ''} ${isAi ? 'ai' : ''}`}
                  >
                    <div
                      className={`message-bubble ${isCmd ? 'command-bubble' : ''} ${isAi ? 'ai-bubble' : ''}`}
                      onContextMenu={(e) => {
                        e.preventDefault();
                        setDeleteMenuId(deleteMenuId === message.id ? null : message.id);
                      }}
                    >
                      {renderMedia(message)}
                      {message.body && <div className="message-text">
                        {isAi && <span className="ai-badge" title="Sent by your AI assistant">✨ AI</span>}
                        {message.isCommand && <span className="command-badge">CMD</span>}
                        {message.isSystemAck && <span className="command-badge ack">ACK</span>}
                        {message.body}
                      </div>}
                      <div className="message-meta">
                        {message.channel === 'whatsapp' && (
                          <span className="msg-channel-badge whatsapp" title="WhatsApp">WA</span>
                        )}
                        {isLocal && (
                          <span className="message-local-badge" title="Sent via NumFree (free)">NF</span>
                        )}
                        <span className="message-time">
                          {formatTime(message.dateCreated)}
                        </span>
                        {isOutbound && (
                          <span className={`message-status ${statusClass(message.status, message.readStatus)}`} title={message.status || 'sent'}>
                            {statusIcon(message.status, message.readStatus)}
                            {message.status && message.status !== 'sent' && <span className="message-status-label">{message.status}</span>}
                          </span>
                        )}
                        {isOutbound && message.sid && (
                          <button
                            className="message-sid-badge"
                            title={'Twilio SID — click to copy\n' + message.sid}
                            onClick={(e) => { e.stopPropagation(); if (navigator.clipboard) navigator.clipboard.writeText(message.sid); }}
                          >
                            {String(message.sid).slice(0, 8)}…
                          </button>
                        )}
                        <button
                          className="message-delete-btn"
                          title="Delete message"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteMenuId(deleteMenuId === message.id ? null : message.id);
                          }}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"/></svg>
                        </button>
                      </div>

                      {deleteMenuId === message.id && (
                        <div className="delete-menu" onClick={(e) => e.stopPropagation()}>
                          <button onClick={() => deleteMessage(message.id, 'me')}>
                            Delete for me
                          </button>
                          {isOutbound && (
                            <button
                              onClick={() => {
                                if (window.confirm('Delete this message for everyone? This cannot be undone.')) {
                                  deleteMessage(message.id, 'everyone');
                                }
                              }}
                              className="delete-everyone"
                            >
                              Delete for everyone
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Attachment preview */}
            {attachment && (
              <div className="attachment-preview">
                {attachmentPreview ? (
                  <img src={attachmentPreview} alt="Preview" className="attachment-thumb" />
                ) : (
                  <div className="attachment-file-info">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                    <span>{attachment.name}</span>
                    <span className="attachment-size">{(attachment.size / 1024).toFixed(0)} KB</span>
                  </div>
                )}
                <button className="attachment-remove" onClick={clearAttachment} title="Remove attachment">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
              </div>
            )}

            <form className="message-composer" onSubmit={sendMessage} data-testid="sms-compose-form">
              {!activeConversation && (
                <input
                  data-testid="sms-contact-input"
                  type="tel"
                  placeholder="Enter phone number..."
                  value={newContact}
                  onChange={(e) => setNewContact(e.target.value)}
                  onBlur={(e) => { var n = normalizeToE164(e.target.value); if (n && n !== e.target.value) setNewContact(n); }}
                  className="contact-input"
                  required
                />
              )}
              <div className="message-input-container">
                <button
                  type="button"
                  className="attach-button"
                  title="Attach image or file"
                  onClick={() => fileInputRef.current && fileInputRef.current.click()}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*,.pdf,.doc,.docx"
                  style={{ display: 'none' }}
                  onChange={handleAttachmentSelect}
                />
                <textarea
                  ref={newMsgInputRef}
                  data-testid="sms-message-input"
                  placeholder={attachment ? "Add a caption..." : "Type a message..."}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    // Enter alone = newline. Cmd/Ctrl+Enter = send. Prevents premature
                    // sends from autocomplete-fired Enter keys.
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                      e.preventDefault();
                      if ((newMessage.trim() || attachment)) {
                        sendMessage(e);
                      }
                    }
                  }}
                  className="message-input"
                  maxLength={1600}
                  rows={1}
                />
                <button
                  data-testid="sms-send-btn"
                  type="submit"
                  className="send-button"
                  disabled={(!newMessage.trim() && !attachment) || (composing && !activeConversation && !newContact.trim())}
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z"/></svg>
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="no-conversation">
            <div className="no-conversation-content">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#ddd" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/></svg>
              <h3>Select a conversation</h3>
              <p>Choose an existing conversation or start a new one</p>
            </div>
          </div>
        )}
      </div>

      {/* Image lightbox */}
      {lightboxUrl && (
        <div className="lightbox-overlay" onClick={() => setLightboxUrl(null)}>
          <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
            <button className="lightbox-close" onClick={() => setLightboxUrl(null)}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            </button>
            <img src={lightboxUrl} alt="Full size" className="lightbox-image" />
          </div>
        </div>
      )}

      {error && (
        <div className="error-toast">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
          {error}
        </div>
      )}
    </div>
  );
};

export default SMSInterface;
